import { Facebook, Instagram, MapPin, MessageCircle, Phone, Youtube } from "lucide-react";
import { LOGO_URL, NAV_LINKS, CATEGORIES, scrollToId, telLink, waLink, DEFAULT_SETTINGS } from "../lib/site";
import { FadeUp, SectionHeading } from "./Reveal";

export function ContactSection({ settings }) {
  const s = { ...DEFAULT_SETTINGS, ...settings };
  return (
    <section id="contact" data-testid="contact-section" className="bg-cream py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          testId="contact-heading"
          eyebrow="Visit or Call"
          title="Connect With Unique Organic Farm"
          subtitle="Call, message or come see the farm for yourself — the gate is always open for visitors."
        />

        <div className="mt-14 grid lg:grid-cols-2 gap-10 lg:gap-14 items-stretch">
          <FadeUp>
            <div className="h-full rounded-3xl bg-forest text-cream p-8 sm:p-12 relative overflow-hidden flex flex-col">
              <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-gold/10" aria-hidden="true" />
              <div className="absolute -left-10 -bottom-14 w-48 h-48 rounded-full bg-goldlight/10" aria-hidden="true" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-goldlight">Call the farm</span>
              <a
                data-testid="contact-phone"
                href={telLink(s)}
                className="mt-3 font-serif text-4xl sm:text-5xl font-semibold tracking-tight hover:text-goldlight transition-colors"
              >
                {s.phone}
              </a>
              <div className="mt-6 flex items-start gap-3 text-cream/75">
                <MapPin className="w-5 h-5 mt-0.5 text-goldlight shrink-0" />
                <p className="text-sm font-light leading-relaxed">
                  {s.address}
                  <span className="block mt-1 text-cream/55">Visitors are welcome — call ahead and we'll show you around.</span>
                </p>
              </div>

              <div className="mt-9 flex flex-col sm:flex-row flex-wrap gap-3.5">
                <a
                  data-testid="get-directions-btn"
                  href={s.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2.5 rounded-full bg-gold px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-nightgreen transition-all duration-400 hover:bg-goldlight hover:-translate-y-0.5"
                >
                  <MapPin className="w-4 h-4" />
                  Get Directions
                </a>
                <a
                  data-testid="call-now-btn"
                  href={telLink(s)}
                  className="inline-flex items-center justify-center gap-2.5 rounded-full bg-cream px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-forest transition-all duration-400 hover:bg-goldlight hover:-translate-y-0.5"
                >
                  <Phone className="w-4 h-4" />
                  Call Now
                </a>
                <a
                  data-testid="whatsapp-btn"
                  href={waLink(s)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2.5 rounded-full border border-cream/40 px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-cream transition-all duration-400 hover:bg-cream hover:text-forest hover:-translate-y-0.5"
                >
                  <MessageCircle className="w-4 h-4" />
                  WhatsApp
                </a>
              </div>

              <div className="mt-auto pt-10 flex items-center gap-4">
                <span className="text-[10px] uppercase tracking-[0.28em] text-cream/50">Follow the farm</span>
                {[
                  { icon: Instagram, href: s.instagram, id: "social-instagram", label: "Instagram" },
                  { icon: Facebook, href: s.facebook, id: "social-facebook", label: "Facebook" },
                  { icon: Youtube, href: s.youtube, id: "social-youtube", label: "YouTube" },
                ].map((soc) => (
                  <a
                    key={soc.id}
                    data-testid={soc.id}
                    href={soc.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={soc.label}
                    className="w-10 h-10 rounded-full border border-cream/25 flex items-center justify-center text-cream/80 transition-all duration-300 hover:bg-gold hover:text-nightgreen hover:border-gold"
                  >
                    <soc.icon className="w-4.5 h-4.5" />
                  </a>
                ))}
              </div>
            </div>
          </FadeUp>

          <FadeUp delay={0.15}>
            <div className="h-full min-h-[340px] rounded-3xl overflow-hidden border border-sand shadow-[0_18px_60px_rgba(17,38,29,0.12)]">
              <iframe
                data-testid="map-embed"
                title="Unique Organic Farm location map"
                src={s.maps_embed || DEFAULT_SETTINGS.maps_embed}
                className="w-full h-full min-h-[340px] lg:min-h-[480px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

export function Footer({ settings }) {
  const s = { ...DEFAULT_SETTINGS, ...settings };
  return (
    <footer data-testid="footer-section" className="bg-nightgreen text-cream/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Unique Organic Farm logo" className="w-12 h-12 rounded-full ring-1 ring-gold/40 object-cover" />
              <span className="font-serif text-xl font-semibold text-cream leading-tight">
                Unique
                <br />
                Organic Farm
              </span>
            </div>
            <p className="mt-4 font-serif italic text-goldlight/90">"Pure From Our Farm to Your Home."</p>
            <p className="mt-3 text-sm font-light leading-relaxed">
              Naturally grown spices, fruits and nursery plants — straight from our fields in the Indian countryside.
            </p>
          </div>

          <nav aria-label="Footer">
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.28em] text-goldlight">Explore</h4>
            <ul className="mt-5 space-y-2.5">
              {NAV_LINKS.map((link) => (
                <li key={link.id}>
                  <button
                    data-testid={`footer-link-${link.id}`}
                    onClick={() => scrollToId(link.id)}
                    className="text-sm font-light hover:text-goldlight transition-colors"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Categories">
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.28em] text-goldlight">Categories</h4>
            <ul className="mt-5 space-y-2.5">
              {CATEGORIES.map((c) => (
                <li key={c.key}>
                  <button
                    data-testid={`footer-category-${c.key}`}
                    onClick={() => scrollToId(c.key)}
                    className="text-sm font-light hover:text-goldlight transition-colors"
                  >
                    {c.label}
                  </button>
                </li>
              ))}
              <li>
                <button data-testid="footer-link-gallery" onClick={() => scrollToId("gallery")} className="text-sm font-light hover:text-goldlight transition-colors">
                  Farm Gallery
                </button>
              </li>
            </ul>
          </nav>

          <div>
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.28em] text-goldlight">Contact</h4>
            <ul className="mt-5 space-y-3 text-sm font-light">
              <li>
                <a data-testid="footer-phone" href={telLink(s)} className="inline-flex items-center gap-2.5 hover:text-goldlight transition-colors">
                  <Phone className="w-4 h-4 text-gold" />
                  {s.phone}
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-directions"
                  href={s.maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2.5 hover:text-goldlight transition-colors"
                >
                  <MapPin className="w-4 h-4 text-gold" />
                  Visit Our Farm
                </a>
              </li>
              <li className="flex items-center gap-3 pt-2">
                {[
                  { icon: Instagram, href: s.instagram, id: "footer-social-instagram", label: "Instagram" },
                  { icon: Facebook, href: s.facebook, id: "footer-social-facebook", label: "Facebook" },
                  { icon: Youtube, href: s.youtube, id: "footer-social-youtube", label: "YouTube" },
                ].map((soc) => (
                  <a
                    key={soc.id}
                    data-testid={soc.id}
                    href={soc.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={soc.label}
                    className="w-9 h-9 rounded-full border border-cream/20 flex items-center justify-center transition-all duration-300 hover:bg-gold hover:text-nightgreen hover:border-gold"
                  >
                    <soc.icon className="w-4 h-4" />
                  </a>
                ))}
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 border-t border-cream/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-cream/50">
          <span data-testid="footer-copyright">© 2026 Unique Organic Farm. All Rights Reserved.</span>
          <span className="font-serif italic">Naturally grown. Carefully nurtured. Truly organic.</span>
        </div>
      </div>
    </footer>
  );
}
